import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Arbeitsschritt, Gefaehrdung, KatalogArbeit, Schutzmassnahme } from './types';
import { MUSTER_ZEILEN, ABSCHNITT_PHASEN } from './musterVorlage';
import { clampDatum, phaseDatum } from './zeitraum';
import { supabase } from '../../services/supabaseClient';
import { Contact } from '../../types';
import { Plus, Trash2, Pencil, X, Save, ListPlus, Loader2, FileText, FilePlus2, Merge, CalendarOff } from 'lucide-react';
import { Button } from '../Button';
import { ergaenzeDokumente, kurzform } from '../../services/mitgeltendeDokumente';
import { MitgeltendeDokumenteEditor } from './MitgeltendeDokumenteEditor';
import {
  aktualisiereVorbelegung,
  auswahlSchluessel,
  baueAuftraege,
  bedingungsHinweis,
  berechneErgaenzungen,
  dialogPayload,
  dokumentSchluessel,
  entfernteDokumente,
  fehlerMeldung,
  istBedingt,
  schreibeErgaenzungen,
  standardAbwahl,
  fuehreErgaenzungZusammen,
  vorbelegungsNamen,
} from './unterlagenErgaenzen';
import type { ErgaenzungsPosten, SchrittDb } from './unterlagenErgaenzen';
import { mitgeltendeFuerNeueZeile } from './uebernahme';
import { reiheSchrittAuftrag } from './schrittSchreibWarteschlange';
import {
  OHNE_ABSCHNITT,
  abschnittVon,
  findeDubletten,
  naechsteSortierung,
  planeAbschnittZusammenfuehren,
  planeVerschieben,
  wendeOrdnungAn,
} from './abschnittOrdnung';
import type { SchrittOrdnung } from './abschnittOrdnung';
import { altMassnahmenAus, fuehreZusammen, schreibeOrdnung, vereinigeSchritte, zusatzFelder } from './schritteZusammenfuehren';
import { SUPABASE_ORDNUNG_DB, SUPABASE_ZUSAMMENFUEHR_DB } from './zusammenfuehrenDb';
import {
  abschnittsZiele,
  dialogBleibtOffen,
  dublettenJeSchritt,
  dublettenTitel,
  formAus,
  gruppiereSchritte,
  klemmeZeitraum,
  ohneZeitraumAnzahl,
  ordnungsFehlerMeldung,
  umschalten,
  zielDerAuswahl,
  zusammenfuehrFehlerText,
  zusammenfuehrHinweis,
} from './arbeitsschritteListeLogik';
import type { SchrittForm } from './arbeitsschritteListeLogik';
import { AbschnittKopf } from './AbschnittKopf';
import { SchrittNeuerAbschnittEingabe, SchrittVerschiebenAuswahl } from './SchrittVerschiebenAuswahl';
import { SchrittZusammenfuehrLeiste } from './SchrittZusammenfuehrLeiste';

/**
 * Datenbankzugriff der Aufträge dieser Liste (Ergänzung, Dialog). Alle
 * Schreibvorgänge auf einen BESTEHENDEN Schritt laufen über die
 * Schreib-Warteschlange (`schrittSchreibWarteschlange.ts`), gemeinsam mit
 * dem Speichern der Detailansicht.
 */
const LISTE_DB: SchrittDb = {
  lese: async (id) => {
    const { data, error } = await supabase.from('sige_arbeitsschritte').select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return (data as Arbeitsschritt | null) ?? null;
  },
  schreibe: async (id, felder) => {
    const { data, error } = await supabase.from('sige_arbeitsschritte').update(felder).eq('id', id).select().single();
    if (error) throw error;
    return (data as Arbeitsschritt | null) ?? null;
  },
};

interface ArbeitsschritteListeProps {
  sigeProjektId?: string;
  schritte: Arbeitsschritt[];
  selectedSchrittId: string | null;
  beteiligte: Contact[];
  projektVon?: string;                 // Projekt-Bauzeit von (Klemm-Untergrenze)
  projektBis?: string;                 // Projekt-Bauzeit bis (Klemm-Obergrenze)
  hauptabschnitte: string[];           // global aus DB (+ Standard)
  katalog: KatalogArbeit[];            // Muster + globale DB-Einträge
  onAbschnittCreated: (name: string) => void;
  onSelectSchritt: (id: string) => void;
  onSchritteChange: (schritte: Arbeitsschritt[]) => void;
  isReadOnly?: boolean;                // Nur-Ansicht: alle schreibenden Bedienelemente weg
  ohneZeitachse?: boolean;             // Plan ohne Bauzeitenplan (darstellung.zeitachse === false)
  gefaehrdungen?: Record<string, Gefaehrdung[]>;    // Altdaten je Schritt-Id
  massnahmen?: Record<string, Schutzmassnahme[]>;   // je Gefährdungs-Id
  /** Autospeichern der Detailansicht abschließen — vor jedem Verschieben/Zusammenführen. */
  onFlush?: () => Promise<void>;
  /** Nach „Abschnitt zusammenführen“: der Quellabschnitt hat keine Schritte mehr. */
  onAbschnittLeer?: (name: string) => void;
  /** Nach „Zusammenführen“: Gefährdungen der gelöschten Zeilen hängen jetzt an der Zielzeile. */
  onGefaehrdungenUmgehaengt?: (vonIds: string[], nachId: string) => void;
}

/** Laufender Verschiebe-/Zusammenführ-Vorgang (Fortschritt beim Abschnitt-Zusammenführen). */
interface Vorgang {
  schrittId?: string;                  // diese Zeile wird verschoben
  abschnitt?: string;                  // dieser Abschnitt wird zusammengeführt
  erledigt: number;
  gesamt: number;
}

/** Fehlertext aus einem unbekannten Fehler. */
const fehlerText = (e: unknown): string =>
  e instanceof Error ? e.message : (typeof e === 'object' && e && 'message' in e ? String(e.message) : String(e));

const EMPTY_FORM: SchrittForm = {
  bezeichnung: '',
  gewerk: '',
  hauptabschnitt: '',
  zeitraum_von: '',
  zeitraum_bis: '',
  schutzmassnahmen: [],
  vorschriften: [],
  mitgeltende_dokumente: [],
  hinweis_code: '',
  hinweis_codes: [],
  kritisch: false,
  koordinierungsbedarf: 'alle Gewerke, Dritte',
  zustaendig_erstellung: '',
  zustaendig_kontrolle: '',
  zustaendig_instandhaltung: '',
};

// Spaltenliste für Insert/Update — was hier fehlt, wird nicht gespeichert.
const formToDb = (f: SchrittForm) => ({
  bezeichnung: f.bezeichnung.trim(),
  gewerk: f.gewerk?.trim() || '',
  hauptabschnitt: f.hauptabschnitt || null,
  zeitraum_von: f.zeitraum_von || null,
  zeitraum_bis: f.zeitraum_bis || null,
  schutzmassnahmen: f.schutzmassnahmen || [],
  vorschriften: f.vorschriften || [],
  mitgeltende_dokumente: f.mitgeltende_dokumente || [],   // Spalte Nr. 156, not null default '{}'
  hinweis_codes: f.hinweis_codes || [],
  hinweis_code: (f.hinweis_codes || []).join('; ') || f.hinweis_code?.trim() || null, // Legacy-Spalte mitpflegen
  kritisch: !!f.kritisch,
  koordinierungsbedarf: f.koordinierungsbedarf?.trim() || null,
  zustaendig_erstellung: f.zustaendig_erstellung?.trim() || null,
  zustaendig_kontrolle: f.zustaendig_kontrolle?.trim() || null,
  zustaendig_instandhaltung: f.zustaendig_instandhaltung?.trim() || null,
});

/** „1 Dokument“ / „3 Dokumente“. */
const anzahl = (n: number, eins: string, mehr: string) => `${n} ${n === 1 ? eins : mehr}`;

// Listen-Editor für Maßnahmen/Vorschriften
const StringListEditor: React.FC<{
  label: string;
  items: string[];
  placeholder: string;
  onChange: (items: string[]) => void;
}> = ({ label, items, placeholder, onChange }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    if (!draft.trim()) return;
    onChange([...items, draft.trim()]);
    setDraft('');
  };
  return (
    <div>
      <label className="block text-xs text-slate-500 mb-1">{label}</label>
      <div className="space-y-1 mb-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded px-2 py-1">
            <span className="flex-1">{it}</span>
            <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          className="flex-1 text-sm p-2 border border-slate-200 rounded outline-none focus:ring-2 focus:ring-blue-500/20"
          placeholder={placeholder}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <Button variant="outline" size="sm" onClick={add}><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

export const ArbeitsschritteListe: React.FC<ArbeitsschritteListeProps> = ({
  sigeProjektId, schritte, selectedSchrittId, beteiligte, projektVon, projektBis, hauptabschnitte, katalog,
  onAbschnittCreated, onSelectSchritt, onSchritteChange,
  isReadOnly = false, ohneZeitachse = false, gefaehrdungen, massnahmen, onFlush, onAbschnittLeer, onGefaehrdungenUmgehaengt,
}) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<SchrittForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importConfirm, setImportConfirm] = useState(false);
  // Kein window.confirm (siehe handleMusterImport) — Löschen wird inline bestätigt.
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neuerAbschnitt, setNeuerAbschnitt] = useState('');
  const [abschnittDialog, setAbschnittDialog] = useState(false);
  const [neuerHinweis, setNeuerHinweis] = useState('');
  const [info, setInfo] = useState<string | null>(null);
  // Neuer Schritt: welche mitgeltenden Dokumente automatisch vorbelegt sind und
  // welche der Nutzer im Dialog schon entfernt hat (die kommen nicht wieder).
  const [vorbelegung, setVorbelegung] = useState<{ automatisch: string[]; abgelehnt: string[] }>({ automatisch: [], abgelehnt: [] });
  // „Unterlagen ergänzen“: Vorschau (null = geschlossen) und Schreibfortschritt.
  const [ergaenzung, setErgaenzung] = useState<{ posten: ErgaenzungsPosten[]; abgewaehlt: ReadonlySet<string> } | null>(null);
  const [ergaenzungLaeuft, setErgaenzungLaeuft] = useState<{ erledigt: number; gesamt: number } | null>(null);
  // Immer der jüngste Stand der Schritte: nach dem Schreiben wird darauf
  // aufgesetzt, nicht auf den Stand beim Klick — die Detailansicht kann
  // derweil selbst gespeichert haben.
  // Layout-Effekt: steht vor jedem neuen Ereignis schon auf dem neuen Stand.
  const schritteRef = useRef(schritte);
  useLayoutEffect(() => { schritteRef.current = schritte; }, [schritte]);
  // Dialog „bearbeiten“: Formularstand beim Öffnen (null = neuer Schritt).
  const formStartRef = useRef<SchrittForm | null>(null);
  // Verschieben / Zusammenführen: die Ref sperrt sofort (Doppelklick), der
  // Zustand sperrt die Knöpfe und trägt den Fortschritt.
  const laeuftRef = useRef(false);
  const [vorgang, setVorgang] = useState<Vorgang | null>(null);
  // Zeile, für die gerade ein neuer Abschnitt eingetippt wird.
  const [neuerAbschnittFuer, setNeuerAbschnittFuer] = useState<string | null>(null);
  // „Zusammenführen“: Auswahlmodus (null = aus) mit den angehakten Zeilen und
  // der gewünschten Zielzeile; im Dialog dann Ziel und Quellen.
  const [auswahl, setAuswahl] = useState<ReadonlySet<string> | null>(null);
  const [zielWunsch, setZielWunsch] = useState<string | null>(null);
  // `stand`: die frisch gelesenen Zeilen, aus denen der Dialog vereinigt wurde — Grundlage der Konfliktprüfung beim Speichern.
  // `zusatz`: vereinigte Spalten, die der Dialog nicht zeigt (zusatzFelder).
  const [zusammenfuehren, setZusammenfuehren] = useState<{
    zielId: string; quellIds: string[]; stand: Arbeitsschritt[]; zusatz: Record<string, unknown>;
  } | null>(null);

  // Bekannte BG-BAU-Baustein-Codes aus dem Katalog als Vorschläge
  const hinweisOptionen = useMemo(() => {
    const set = new Set<string>();
    katalog.forEach(k => (k.hinweis_codes || []).forEach(c => set.add(c)));
    return Array.from(set).sort();
  }, [katalog]);

  // Zuständigkeits-Vorschläge aus Projektbeteiligten: "Gewerk (Firma)"
  const zustaendigOptionen = useMemo(() => {
    const opts = new Set<string>([
      'Bauherr', 'Auftragnehmer (AN)', 'alle Gewerke', 'alle Gewerke, Dritte', 'jeweiliges Gewerk', 'Elektrofachkraft',
    ]);
    beteiligte.forEach(b => {
      const gewerk = b.tradeType || b.role;
      if (gewerk && b.company) opts.add(`${gewerk} (${b.company})`);
      else if (b.company) opts.add(b.company);
    });
    return Array.from(opts);
  }, [beteiligte]);

  const gruppen = useMemo(() => gruppiereSchritte(schritte, hauptabschnitte), [schritte, hauptabschnitte]);
  // Ziele fürs Verschieben und für den Abschnitt im Dialog
  const ziele = useMemo(() => abschnittsZiele(hauptabschnitte, schritte), [hauptabschnitte, schritte]);
  const dubletten = useMemo(() => dublettenJeSchritt(findeDubletten(schritte)), [schritte]);
  const schrittNachId = useMemo(() => new Map(schritte.map(s => [s.id, s])), [schritte]);
  const ohneZeitraum = useMemo(() => ohneZeitraumAnzahl(schritte), [schritte]);

  // Auswahlmodus: gewählte Zeilen in Listenreihenfolge (nicht Klickreihenfolge;
  // Verschwundenes fällt heraus), Zielzeile (Vorgabe: die erste)
  const gewaehlt = useMemo(
    () => (auswahl ? gruppen.flatMap(g => g.schritte).filter(s => auswahl.has(s.id)) : []),
    [auswahl, gruppen],
  );
  const zielId = zielDerAuswahl(gewaehlt.map(s => s.id), zielWunsch);
  const auswahlModus = auswahl !== null;
  const gesperrt = !!vorgang;

  /**
   * Neuer Schritt: mitgeltende Dokumente zu Bezeichnung und Gewerk laufend
   * vorbelegen — nur Vorschläge OHNE Bedingung; bedingte („nur im Bestand“)
   * bietet der Editor an, der Bearbeiter entscheidet. Beim Bearbeiten eines
   * bestehenden Schritts nie — dort stehen die Vorschläge nur zur Übernahme
   * im Editor.
   */
  const mitVorbelegung = (next: SchrittForm, vb = vorbelegung): SchrittForm => {
    if (editingId) return next;
    const vorschlaege = vorbelegungsNamen({
      bezeichnung: next.bezeichnung,
      gewerk: next.gewerk,
      schutzmassnahmen: next.schutzmassnahmen,
      // Was schon unter den Vorschriften steht, nicht noch einmal vorbelegen.
      vorschriften: next.vorschriften,
    });
    const r = aktualisiereVorbelegung(next.mitgeltende_dokumente || [], vb.automatisch, vorschlaege, vb.abgelehnt);
    setVorbelegung({ ...vb, automatisch: r.automatisch });
    return { ...next, mitgeltende_dokumente: r.dokumente };
  };

  // Katalog-Treffer: leere Felder aus dem Katalog-Eintrag vorbefüllen
  const handleBezeichnung = (val: string) => {
    const f = form;
    const treffer = katalog.find(k => k.bezeichnung === val);
    if (!treffer) {
      setForm(mitVorbelegung({ ...f, bezeichnung: val }));
      return;
    }
    // Die Dokumente des Katalogeintrags kommen dazu (nie ersetzen) und gelten
    // als bewusst gewählt — die Vorbelegung nimmt sie nicht wieder heraus.
    const katalogDokumente = treffer.mitgeltende_dokumente || [];
    const ausKatalog = new Set(katalogDokumente.map(dokumentSchluessel));
    const vb = { ...vorbelegung, automatisch: vorbelegung.automatisch.filter(a => !ausKatalog.has(dokumentSchluessel(a))) };
    setForm(mitVorbelegung({
      ...f,
      bezeichnung: val,
      hauptabschnitt: treffer.hauptabschnitt || f.hauptabschnitt,
      gewerk: f.gewerk || treffer.gewerk || '',
      schutzmassnahmen: f.schutzmassnahmen?.length ? f.schutzmassnahmen : (treffer.schutzmassnahmen || []),
      vorschriften: f.vorschriften?.length ? f.vorschriften : (treffer.vorschriften || []),
      mitgeltende_dokumente: ergaenzeDokumente(f.mitgeltende_dokumente || [], katalogDokumente),
      hinweis_code: f.hinweis_code || treffer.hinweis_code || '',
      hinweis_codes: f.hinweis_codes?.length ? f.hinweis_codes : (treffer.hinweis_codes || []),
      koordinierungsbedarf: treffer.koordinierungsbedarf || f.koordinierungsbedarf,
      zustaendig_erstellung: f.zustaendig_erstellung || treffer.zustaendig_erstellung || '',
      zustaendig_kontrolle: f.zustaendig_kontrolle || treffer.zustaendig_kontrolle || '',
      zustaendig_instandhaltung: f.zustaendig_instandhaltung || treffer.zustaendig_instandhaltung || '',
    }, vb));
  };

  // Änderung im Dokument-Editor des Dialogs: Entferntes merken, damit die
  // Vorbelegung es beim Weitertippen nicht wieder einträgt.
  const handleDokumente = (dokumente: string[]) => {
    const entfernt = entfernteDokumente(form.mitgeltende_dokumente || [], dokumente);
    const bleibt = new Set(dokumente.map(dokumentSchluessel));
    setVorbelegung(vb => ({
      automatisch: vb.automatisch.filter(a => bleibt.has(dokumentSchluessel(a))),
      abgelehnt: [...vb.abgelehnt, ...entfernt],
    }));
    setForm({ ...form, mitgeltende_dokumente: dokumente });
  };

  /**
   * Abschnitt global anlegen (23505 = existiert schon, ok) und im Plan
   * registrieren. Scheitert die globale Tabelle, gilt er trotzdem für dieses
   * Projekt.
   */
  const registriereAbschnitt = async (name: string) => {
    try {
      const { error } = await supabase.from('sige_hauptabschnitte')
        .insert({ name, sortierung: 100 + hauptabschnitte.length * 10 });
      if (error && error.code !== '23505') throw error;
    } catch (e) {
      console.warn('Could not save section globally. Saving locally to this project:', e);
    }
    onAbschnittCreated(name);
  };

  // Neuen Hauptabschnitt im Dialog anlegen und gleich zuweisen
  const handleNeuerAbschnitt = async () => {
    const name = neuerAbschnitt.trim();
    if (!name) return;
    await registriereAbschnitt(name);
    setForm(f => ({ ...f, hauptabschnitt: name }));
    setNeuerAbschnitt('');
    setAbschnittDialog(false);
  };

  const openNew = () => {
    if (isReadOnly) return;
    setFehler(null);
    setEditingId(null);
    setZusammenfuehren(null);
    formStartRef.current = null;
    setForm(EMPTY_FORM);
    setVorbelegung({ automatisch: [], abgelehnt: [] });
    setEditorOpen(true);
  };

  const openEdit = (s: Arbeitsschritt, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isReadOnly) return;
    setFehler(null);
    setEditingId(s.id);
    setZusammenfuehren(null);
    setVorbelegung({ automatisch: [], abgelehnt: [] });
    const startForm = formAus(s);
    // Stand beim Öffnen: beim Speichern gehen nur die im Dialog geänderten Spalten raus.
    formStartRef.current = startForm;
    setForm(startForm);
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (isReadOnly || !form.bezeichnung.trim()) return;
    if (!sigeProjektId) {
      setFehler('Speichern nicht möglich: Es ist kein gültiger SiGe-Plan-Projektdatensatz geladen. Bitte laden Sie die Seite neu.');
      return;
    }
    setSaving(true);
    // Zeiträume dürfen nie vor Projekt-Start / nach Projekt-Ende liegen
    const clampedForm = klemmeZeitraum(form, ohneZeitachse, projektVon, projektBis);
    try {
      if (editingId) {
        // Über die Schreib-Warteschlange des Schritts: läuft nach einem noch
        // ausstehenden Speichern der Detailansicht und schreibt nur, was im
        // Dialog geändert wurde — Listen dreiwege gegen die frische Zeile.
        const id = editingId;
        const start = formToDb(formStartRef.current ?? clampedForm);
        const jetzt = formToDb(clampedForm);
        const data = await reiheSchrittAuftrag(id, async () => {
          const zeile = await LISTE_DB.lese(id);
          if (!zeile) throw new Error('Der Arbeitsschritt existiert nicht mehr.');
          const payload = dialogPayload(start, jetzt, zeile);
          if (!Object.keys(payload).length) return zeile;
          return LISTE_DB.schreibe(id, payload as Partial<Arbeitsschritt>);
        });
        if (data) onSchritteChange(schritteRef.current.map(s => s.id === id ? data : s));
      } else {
        // Hinter die höchste Sortierung, nicht `schritte.length`: nach
        // Verschiebungen landete die Zeile beim Neuladen sonst davor.
        const { data, error } = await supabase.from('sige_arbeitsschritte')
          .insert({ sige_projekt_id: sigeProjektId, sortierung: naechsteSortierung(schritteRef.current), ...formToDb(clampedForm) })
          .select().single();
        if (error) throw error;
        if (data) {
          onSchritteChange([...schritteRef.current, data as Arbeitsschritt]);
          onSelectSchritt((data as Arbeitsschritt).id);
        }
      }
      setEditorOpen(false);
    } catch (e: any) {
      console.error('Fehler beim Speichern des Arbeitsschritts', e);
      setFehler(`Speichern fehlgeschlagen: ${e.message || 'Prüfen Sie, ob die DB-Migration vollständig eingespielt ist.'}`);
    } finally {
      setSaving(false);
    }
  };

  // Erst nach Inline-Bestätigung; Fehler landen im Banner statt nur in der Konsole.
  const handleDelete = async (id: string) => {
    if (isReadOnly) return;
    setFehler(null);
    setDeletingId(id);
    try {
      // Auch das Löschen reiht sich ein: ein danach noch laufender Auftrag
      // findet die Zeile nicht mehr und schreibt nichts.
      await reiheSchrittAuftrag(id, async () => {
        const { error } = await supabase.from('sige_arbeitsschritte').delete().eq('id', id);
        if (error) throw error;
      });
      onSchritteChange(schritteRef.current.filter(s => s.id !== id));
      if (selectedSchrittId === id) onSelectSchritt('');
      setDeleteConfirmId(null);
    } catch (e: any) {
      console.error('Löschen des Arbeitsschritts fehlgeschlagen', e);
      setFehler(`Löschen fehlgeschlagen: ${e?.message || 'Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.'}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Kein window.confirm — App läuft ggf. in Sandbox-iframe (AI Studio), Modals dort blockiert
  const handleMusterImport = async (clearExisting = false) => {
    if (isReadOnly) return;
    if (!sigeProjektId) {
      // Ohne Plan-Datensatz fehlt der Fremdschlüssel — ohne Meldung sähe der
      // Klick auf „Übernehmen" wie ein stiller Fehlschlag aus.
      setImportConfirm(false);
      setFehler('Import nicht möglich: Für dieses Projekt ist noch kein SiGe-Plan angelegt. Bitte zuerst den Assistenten durchlaufen.');
      return;
    }
    setImportConfirm(false);
    setFehler(null);
    setImporting(true);
    try {
      if (clearExisting) {
        const { error: delError } = await supabase
          .from('sige_arbeitsschritte')
          .delete()
          .eq('sige_projekt_id', sigeProjektId);
        if (delError) throw delError;
      }

      const baseSortIndex = clearExisting ? 0 : naechsteSortierung(schritteRef.current);

      const rows = MUSTER_ZEILEN.map((m, i) => {
        // typische Phase → echtes Datum, auf Projektgrenzen geklemmt. Ohne
        // Bauzeitenplan keine Phasendaten: sie wären geraten, nicht geplant.
        const phase = ohneZeitachse ? undefined : ABSCHNITT_PHASEN[m.hauptabschnitt || ''];
        const von = phase ? clampDatum(phaseDatum(phase[0], projektVon, projektBis), projektVon, projektBis) : '';
        const bis = phase ? clampDatum(phaseDatum(phase[1], projektVon, projektBis), projektVon, projektBis) : '';
        return {
          sige_projekt_id: sigeProjektId,
          sortierung: baseSortIndex + i,
          // Mitgeltende Dokumente: die der Vorlage, ergänzt um die Katalogvorschläge.
          // formToDb setzt die Spalte in JEDER Zeile (notfalls []) — beim
          // Sammel-Insert würde ein fehlender Schlüssel sonst zu NULL.
          ...formToDb({
            ...EMPTY_FORM,
            ...m,
            mitgeltende_dokumente: mitgeltendeFuerNeueZeile({
              bezeichnung: m.bezeichnung,
              gewerk: m.gewerk,
              schutzmassnahmen: m.schutzmassnahmen,
              vorschriften: m.vorschriften,
              mitgeltende_dokumente: m.mitgeltende_dokumente,
            }),
            zeitraum_von: von,
            zeitraum_bis: bis,
          }),
        };
      });

      const { data, error } = await supabase.from('sige_arbeitsschritte').insert(rows).select();
      if (error) throw error;
      if (data) {
        const imported = data as Arbeitsschritt[];
        if (clearExisting) {
          onSchritteChange(imported);
          if (imported.length > 0) onSelectSchritt(imported[0].id);
        } else {
          onSchritteChange([...schritteRef.current, ...imported]);
        }
      }
    } catch (e: any) {
      console.error('Muster-Import fehlgeschlagen', e);
      setFehler('Import fehlgeschlagen: ' + (e.message || e));
    } finally {
      setImporting(false);
    }
  };

  // ─── „Unterlagen ergänzen“: Vorschläge für alle Schritte, nur ergänzen ───

  const oeffneErgaenzung = () => {
    if (isReadOnly) return;
    setFehler(null);
    setInfo(null);
    setImportConfirm(false);
    // Bedingte Vorschläge erscheinen, sind aber nicht vorausgewählt.
    const posten = berechneErgaenzungen(schritte);
    setErgaenzung({ posten, abgewaehlt: standardAbwahl(posten) });
  };

  const umschaltenAuswahl = (schrittId: string, name: string) => {
    setErgaenzung(e => {
      if (!e) return e;
      const abgewaehlt = new Set(e.abgewaehlt);
      const k = auswahlSchluessel(schrittId, name);
      if (abgewaehlt.has(k)) abgewaehlt.delete(k); else abgewaehlt.add(k);
      return { ...e, abgewaehlt };
    });
  };

  const alleAuswaehlen = (an: boolean) => {
    setErgaenzung(e => e && {
      ...e,
      abgewaehlt: an
        ? new Set()
        : new Set(e.posten.flatMap(p => p.neu.map(v => auswahlSchluessel(p.schrittId, v.dokument.name)))),
    });
  };

  const ergaenzungAnwenden = async () => {
    if (!ergaenzung || ergaenzungLaeuft) return;
    // Gegen den jüngsten Stand rechnen, nicht gegen den beim Öffnen der Vorschau.
    const auftraege = baueAuftraege(ergaenzung.posten, ergaenzung.abgewaehlt, schritteRef.current);
    if (!auftraege.length) {
      setErgaenzung(null);
      setInfo('Keine Ergänzung nötig – die gewählten Dokumente sind bereits eingetragen.');
      return;
    }
    setErgaenzungLaeuft({ erledigt: 0, gesamt: auftraege.length });
    try {
      // Je Schritt über die Schreib-Warteschlange; jeder Auftrag liest die
      // frische Zeile und ergänzt erst dann — was die Detailansicht vorher
      // gespeichert hat, bleibt enthalten.
      const ergebnis = await schreibeErgaenzungen(
        auftraege,
        LISTE_DB,
        (erledigt, gesamt) => setErgaenzungLaeuft({ erledigt, gesamt }),
      );
      if (ergebnis.geschrieben.length) {
        // Den AKTUELLEN Stand um das Hinzugefügte ergänzen — nur die Spalte
        // `mitgeltende_dokumente`, nie ganze Antwortzeilen.
        onSchritteChange(fuehreErgaenzungZusammen(schritteRef.current, ergebnis.geschrieben));
        setInfo(`Unterlagen ergänzt: ${anzahl(ergebnis.dokumenteGeschrieben, 'Dokument', 'Dokumente')} in ${anzahl(ergebnis.geschrieben.length, 'Arbeitsschritt', 'Arbeitsschritten')}.`);
      }
      if (ergebnis.fehler.length) {
        console.error('Unterlagen ergänzen: einzelne Schritte nicht gespeichert', ergebnis.fehler);
        setFehler(fehlerMeldung(ergebnis.fehler));
      }
      setErgaenzung(null);
    } finally {
      setErgaenzungLaeuft(null);
    }
  };

  // Anzahl der in der Vorschau gewählten Dokumente und der betroffenen Schritte
  const ergaenzungAuswahl = useMemo(() => {
    if (!ergaenzung) return { dokumente: 0, schritte: 0 };
    let dokumente = 0;
    let schritteMitAuswahl = 0;
    ergaenzung.posten.forEach(p => {
      const n = p.neu.filter(v => !ergaenzung.abgewaehlt.has(auswahlSchluessel(p.schrittId, v.dokument.name))).length;
      dokumente += n;
      if (n) schritteMitAuswahl += 1;
    });
    return { dokumente, schritte: schritteMitAuswahl };
  }, [ergaenzung]);

  // ─── Verschieben und Zusammenführen ───

  const starteVorgang = (v: Vorgang): boolean => {
    if (laeuftRef.current) return false;
    laeuftRef.current = true;
    setVorgang(v);
    return true;
  };
  const beendeVorgang = () => {
    laeuftRef.current = false;
    setVorgang(null);
  };

  /**
   * Offenes Autospeichern der Detailansicht zuerst schreiben — sonst ginge
   * die letzte Änderung einer danach gelöschten Zeile verloren. Scheitert
   * es, bricht der Vorgang ab.
   */
  const flushOderAbbruch = async (was: string): Promise<boolean> => {
    try {
      await onFlush?.();
      return true;
    } catch (e) {
      console.error(`${was}: Autospeichern der Detailansicht fehlgeschlagen`, e);
      setFehler(`${was} abgebrochen: Die letzte Änderung in der Detailansicht konnte nicht gespeichert werden. Bitte Verbindung prüfen und erneut versuchen.`);
      return false;
    }
  };

  /**
   * Neue Ordnung (Abschnitt + Sortierung) planen und schreiben — je Schritt
   * über die Schreib-Warteschlange. Geplant wird gegen den jüngsten Stand.
   * Ergebnis: der Stand danach (null = nicht gelaufen) und ob alles ankam.
   */
  const schreibeNeueOrdnung = async (
    plane: (aktuell: Arbeitsschritt[]) => SchrittOrdnung[],
    v: Vorgang,
  ): Promise<{ stand: Arbeitsschritt[]; vollstaendig: boolean } | null> => {
    if (isReadOnly || !starteVorgang(v)) return null;
    setFehler(null);
    setInfo(null);
    try {
      if (!(await flushOderAbbruch('Verschieben'))) return null;
      const ordnung = plane(schritteRef.current);
      if (!ordnung.length) return { stand: schritteRef.current, vollstaendig: true };
      const ergebnis = await schreibeOrdnung(
        ordnung,
        SUPABASE_ORDNUNG_DB,
        (erledigt, gesamt) => setVorgang(alt => alt && { ...alt, erledigt, gesamt }),
      );
      // Nur die zwei Felder auf den AKTUELLEN Stand legen, nie ganze Zeilen.
      const stand = ergebnis.geschrieben.length ? wendeOrdnungAn(schritteRef.current, ergebnis.geschrieben) : schritteRef.current;
      if (ergebnis.geschrieben.length) onSchritteChange(stand);
      if (ergebnis.fehler.length) {
        console.error('Verschieben: einzelne Schritte nicht gespeichert', ergebnis.fehler);
        setFehler(ordnungsFehlerMeldung(ergebnis.fehler, schritteRef.current));
      }
      return { stand, vollstaendig: !ergebnis.fehler.length };
    } catch (e) {
      // schreibeOrdnung wirft nie — hier landen nur Fehler der Planung.
      console.error('Verschieben fehlgeschlagen', e);
      setFehler(`Verschieben fehlgeschlagen: ${fehlerText(e)}`);
      return null;
    } finally {
      beendeVorgang();
    }
  };

  const verschiebeSchritt = async (id: string, ziel: string) => {
    await schreibeNeueOrdnung(aktuell => planeVerschieben(aktuell, id, ziel), { schrittId: id, erledigt: 0, gesamt: 0 });
  };

  // „Neuer Abschnitt …“ aus der Verschiebe-Auswahl: erst im Plan registrieren, dann verschieben.
  const verschiebeInNeuenAbschnitt = async (id: string, name: string) => {
    if (isReadOnly || laeuftRef.current || !name.trim()) return;
    setNeuerAbschnittFuer(null);
    await registriereAbschnitt(name.trim());
    await verschiebeSchritt(id, name.trim());
  };

  // Alle Schritte eines Abschnitts in einen anderen; danach steht der Quellabschnitt ggf. leer.
  const fuehreAbschnittZusammen = async (quelle: string, ziel: string): Promise<boolean> => {
    const r = await schreibeNeueOrdnung(
      aktuell => planeAbschnittZusammenfuehren(aktuell, quelle, ziel),
      { abschnitt: quelle, erledigt: 0, gesamt: 0 },
    );
    if (!r) return false;
    if (quelle && !r.stand.some(s => abschnittVon(s) === quelle)) onAbschnittLeer?.(quelle);
    if (r.vollstaendig) setInfo(`„${quelle || OHNE_ABSCHNITT}“ mit „${ziel || OHNE_ABSCHNITT}“ zusammengeführt.`);
    return r.vollstaendig;
  };

  /** Auswahlmodus „Zusammenführen“ an — optional mit vorgewählten Zeilen (Dubletten-Marke). */
  const starteAuswahl = (ids: string[] = []) => {
    if (isReadOnly || laeuftRef.current) return;
    setAuswahl(new Set(ids));
    setZielWunsch(null);
    setDeleteConfirmId(null);
    setNeuerAbschnittFuer(null);
    setImportConfirm(false);
    setFehler(null);
    setInfo(null);
  };

  const beendeAuswahl = () => {
    setAuswahl(null);
    setZielWunsch(null);
    setZusammenfuehren(null);
  };

  /** Dialog schließen; im Zusammenführen-Modus endet damit auch die Auswahl — es wird nichts geschrieben. */
  const schliesseEditor = () => {
    setEditorOpen(false);
    if (zusammenfuehren) beendeAuswahl();
  };

  /**
   * „Weiter“: Ziel und Quellen frisch lesen — über die Schreib-Warteschlange,
   * also nach dem, was die Detailansicht eben noch geschrieben hat — und den
   * Bearbeiten-Dialog mit den vereinigten Feldern öffnen.
   */
  const weiterZumZusammenfuehren = async () => {
    if (isReadOnly || !zielId || gewaehlt.length < 2) return;
    const quellIds = gewaehlt.map(s => s.id).filter(id => id !== zielId);
    if (!starteVorgang({ erledigt: 0, gesamt: 0 })) return;
    setFehler(null);
    setInfo(null);
    try {
      if (!(await flushOderAbbruch('Zusammenführen'))) return;
      const [ziel, ...rest] = await Promise.all(
        [zielId, ...quellIds].map(id => reiheSchrittAuftrag(id, () => LISTE_DB.lese(id))),
      );
      const quellen = rest.filter((q): q is Arbeitsschritt => !!q);
      if (!ziel || quellen.length !== quellIds.length) {
        setFehler('Zusammenführen nicht möglich: Ein gewählter Arbeitsschritt existiert nicht mehr. Bitte laden Sie die Seite neu.');
        return;
      }
      const vereint = vereinigeSchritte(ziel, quellen, altMassnahmenAus(gefaehrdungen ?? {}, massnahmen ?? {}));
      const start = klemmeZeitraum(formAus(vereint), ohneZeitachse, projektVon, projektBis);
      // Wie beim Bearbeiten: `editingId` gesetzt → keine Vorbelegung mitgeltender Dokumente.
      setEditingId(zielId);
      setVorbelegung({ automatisch: [], abgelehnt: [] });
      formStartRef.current = start;
      setForm(start);
      setAbschnittDialog(false);
      setZusammenfuehren({ zielId, quellIds, stand: [ziel, ...quellen], zusatz: zusatzFelder(vereint, ziel) });
      setEditorOpen(true);
    } catch (e) {
      console.error('Zusammenführen: Zeilen nicht lesbar', e);
      setFehler(`Zusammenführen nicht möglich: ${fehlerText(e)}`);
    } finally {
      beendeVorgang();
    }
  };

  /**
   * Speichern im Zusammenführen-Modus: Zielzeile schreiben, Gefährdungen
   * umhängen, Quellen löschen (`fuehreZusammen`, wirft nie). Was davon in der
   * Datenbank ankam, übernimmt die Liste — auch bei einem Fehler in einer
   * späteren Stufe.
   */
  const handleZusammenfuehren = async () => {
    const zf = zusammenfuehren;
    if (isReadOnly || !zf || !form.bezeichnung.trim()) return;
    if (!starteVorgang({ erledigt: 0, gesamt: 0 })) return;
    setSaving(true);
    setFehler(null);
    try {
      if (!(await flushOderAbbruch('Zusammenführen'))) return;
      const felder = { ...formToDb(klemmeZeitraum(form, ohneZeitachse, projektVon, projektBis)), ...zf.zusatz };
      const ergebnis = await fuehreZusammen(zf.zielId, zf.quellIds, felder, SUPABASE_ZUSAMMENFUEHR_DB, zf.stand);
      const ziel = ergebnis.ziel;
      const weg = new Set(ergebnis.geloescht);
      if (ziel || weg.size) {
        onSchritteChange(schritteRef.current.filter(s => !weg.has(s.id)).map(s => (ziel && s.id === ziel.id ? ziel : s)));
      }
      // Scheiterte das Umhängen mittendrin, ist unbekannt, welche Gefährdungen
      // schon an der Zielzeile hängen — dann lokal nichts nachziehen.
      const teilweiseUmgehaengt = ergebnis.fehler?.stufe === 'gefaehrdungen' && ergebnis.umgehaengt > 0;
      if (ergebnis.umgehaengt > 0 && !teilweiseUmgehaengt) onGefaehrdungenUmgehaengt?.(zf.quellIds, zf.zielId);
      if (ergebnis.fehler) {
        console.error('Zusammenführen fehlgeschlagen', ergebnis.fehler);
        setFehler(zusammenfuehrFehlerText(ergebnis.fehler.stufe, ergebnis.fehler.meldung)
          + (teilweiseUmgehaengt ? ' Bitte die Seite neu laden, um die Gefährdungen im aktuellen Stand zu sehen.' : ''));
        // Noch nichts geschrieben: der Dialog bleibt offen, erneut versuchen geht.
        if (dialogBleibtOffen(ergebnis.fehler.stufe)) return;
        // Veraltete Vereinigung: Dialog zu, Auswahl und Zielwahl bleiben —
        // „Weiter“ liest frisch und vereinigt neu.
        if (ergebnis.fehler.stufe === 'konflikt') {
          setEditorOpen(false);
          setZusammenfuehren(null);
          return;
        }
      } else {
        setInfo(`${anzahl(weg.size + 1, 'Arbeitsschritt', 'Arbeitsschritte')} zu „${ziel?.bezeichnung ?? form.bezeichnung.trim()}“ zusammengeführt${ergebnis.umgehaengt ? `, ${anzahl(ergebnis.umgehaengt, 'Gefährdung', 'Gefährdungen')} übernommen` : ''}.`);
      }
      onSelectSchritt(zf.zielId);
      setEditorOpen(false);
      beendeAuswahl();
    } finally {
      setSaving(false);
      beendeVorgang();
    }
  };

  const inputCls = 'w-full text-sm p-2 border border-slate-200 rounded outline-none focus:ring-2 focus:ring-blue-500/20';

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h2 className="font-semibold text-slate-800">Arbeitsschritte</h2>
          {!ohneZeitachse && ohneZeitraum > 0 && (
            <span
              className="inline-flex items-center gap-1 text-[11px] text-amber-700 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200"
              title={`${anzahl(ohneZeitraum, 'Arbeitsschritt hat', 'Arbeitsschritte haben')} keinen vollständigen Zeitraum (von und bis) – der Plan zeichnet für sie keinen Balken in der Zeitachse. Zeitraum über den Stift nachtragen; liegt noch kein Bauzeitenplan vor, die Zeitachse im Stempelfeld abschalten.`}
            >
              <CalendarOff className="w-3 h-3" />
              {ohneZeitraum} ohne Zeitraum
            </span>
          )}
        </div>
        {!isReadOnly && (
          <div className="flex flex-wrap items-center justify-end gap-1 ml-auto">
            <button
              onClick={oeffneErgaenzung}
              disabled={schritte.length === 0 || !!ergaenzungLaeuft || gesperrt || auswahlModus}
              className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Mitgeltende Dokumente für alle Arbeitsschritte aus dem Katalog vorschlagen – mit Vorschau, es wird nur ergänzt"
            >
              {ergaenzungLaeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FilePlus2 className="w-3.5 h-3.5" />}
              Unterlagen ergänzen
            </button>
            <button
              onClick={() => setImportConfirm(!importConfirm)}
              disabled={importing || gesperrt || auswahlModus}
              className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Standard-Zeilen aus Muster-Vorlage übernehmen"
            >
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListPlus className="w-3.5 h-3.5" />}
              Muster
            </button>
            <button
              onClick={() => (auswahlModus ? beendeAuswahl() : starteAuswahl())}
              disabled={(!auswahlModus && schritte.length < 2) || gesperrt}
              className={`text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                auswahlModus ? 'bg-blue-600 text-white hover:bg-blue-700' : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
              }`}
              title={auswahlModus ? 'Auswahlmodus beenden, ohne etwas zu ändern' : 'Doppelte Arbeitsschritte zu einer Zeile zusammenführen'}
              aria-pressed={auswahlModus}
            >
              <Merge className="w-3.5 h-3.5" />
              Zusammenführen
            </button>
          </div>
        )}
      </div>

      {auswahlModus && !isReadOnly && (
        <SchrittZusammenfuehrLeiste
          gewaehlt={gewaehlt}
          zielId={zielId}
          gesperrt={gesperrt}
          onZiel={setZielWunsch}
          onWeiter={weiterZumZusammenfuehren}
          onAbbrechen={beendeAuswahl}
        />
      )}

      {importConfirm && !isReadOnly && (
        <div className="p-3 bg-blue-50 border-b border-blue-200 text-xs text-blue-900 space-y-2">
          <div>
            <strong>Muster-Arbeitsschritte importieren ({MUSTER_ZEILEN.length} Zeilen)</strong>
            {schritte.length > 0 && (
              <p className="mt-1 text-[11px] text-amber-800">
                Achtung: Es sind bereits {schritte.length} Arbeitsschritte in diesem Projekt vorhanden.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {schritte.length > 0 ? (
              <>
                <Button variant="primary" size="sm" onClick={() => handleMusterImport(false)}>
                  Hinzufügen (+{MUSTER_ZEILEN.length})
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleMusterImport(true)} className="border-red-300 text-red-700 hover:bg-red-50">
                  Bestehende leeren & Überschreiben
                </Button>
              </>
            ) : (
              <Button variant="primary" size="sm" onClick={() => handleMusterImport(false)}>
                Übernehmen
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setImportConfirm(false)}>
              Abbrechen
            </Button>
          </div>
        </div>
      )}

      {fehler && (
        <div className="p-2 bg-red-50 border-b border-red-200 text-xs text-red-700 flex justify-between items-center">
          <span>{fehler}</span>
          <button onClick={() => setFehler(null)} className="text-red-400 hover:text-red-600 px-1">✕</button>
        </div>
      )}

      {info && (
        <div className="p-2 bg-blue-50 border-b border-blue-200 text-xs text-blue-800 flex justify-between items-center gap-2">
          <span>{info}</span>
          <button onClick={() => setInfo(null)} className="text-blue-400 hover:text-blue-600 px-1">✕</button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {gruppen.map(gruppe => (
          // Schlüssel mit Präfix: '' (ohne Abschnitt) wäre sonst ein leerer Key
          <div key={`ab:${gruppe.schluessel}`}>
            <AbschnittKopf
              name={gruppe.name}
              schluessel={gruppe.schluessel}
              anzahl={gruppe.schritte.length}
              ziele={ziele}
              bearbeitbar={!isReadOnly && !auswahlModus}
              gesperrt={gesperrt}
              fortschritt={vorgang && vorgang.abschnitt === gruppe.schluessel ? vorgang : null}
              onZusammenfuehren={ziel => fuehreAbschnittZusammen(gruppe.schluessel, ziel)}
            />
            <div className="space-y-1">
              {gruppe.schritte.map(schritt => {
                const dublette = dubletten.get(schritt.id);
                const dublettenHinweis = dublette
                  ? dublettenTitel(dublette.flatMap(id => schrittNachId.get(id) ?? []), !isReadOnly)
                  : undefined;
                const angehakt = !!auswahl?.has(schritt.id);
                // Nur-Ansicht und Auswahlmodus: keine Knöpfe an der Zeile
                const zeilenKnoepfe = !isReadOnly && !auswahlModus;
                return (
                <React.Fragment key={schritt.id}>
                <div
                  onClick={() => {
                    // Im Auswahlmodus schaltet der Klick das Häkchen statt die Detailansicht
                    if (!auswahlModus) onSelectSchritt(schritt.id);
                    else if (!gesperrt) setAuswahl(a => a && umschalten(a, schritt.id));
                  }}
                  className={`p-2.5 rounded-lg border cursor-pointer transition-all flex items-start gap-2 group ${
                    auswahlModus
                      ? (angehakt ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-500/20' : 'bg-white border-slate-200 hover:border-slate-300')
                      : selectedSchrittId === schritt.id
                        ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-500/20'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {auswahlModus && (
                    <input
                      type="checkbox"
                      className="mt-1 shrink-0"
                      checked={angehakt}
                      disabled={gesperrt}
                      onClick={e => e.stopPropagation()}
                      onChange={() => setAuswahl(a => a && umschalten(a, schritt.id))}
                      aria-label={`„${schritt.bezeichnung}“ zum Zusammenführen wählen`}
                    />
                  )}
                  <div className="flex-1 overflow-hidden">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className={`font-medium text-sm truncate ${schritt.kritisch ? 'text-red-700' : 'text-slate-800'}`}>
                        {schritt.bezeichnung}
                      </div>
                      {dublette && (isReadOnly ? (
                        <span className="shrink-0 text-[10px] font-semibold text-amber-800 px-1 rounded bg-amber-50 border border-amber-300" title={dublettenHinweis}>
                          {dublette.length}×
                        </span>
                      ) : (
                        // Klick: Auswahlmodus mit genau dieser Gruppe
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); starteAuswahl(dublette); }}
                          disabled={gesperrt}
                          className="shrink-0 text-[10px] font-semibold text-amber-800 px-1 rounded bg-amber-50 border border-amber-300 hover:bg-amber-100 disabled:opacity-50"
                          title={dublettenHinweis}
                        >
                          {dublette.length}×
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {schritt.gewerk && (
                        <span className="text-[11px] text-slate-500 px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">{schritt.gewerk}</span>
                      )}
                      {/* Mitgeltende Dokumente statt der BG-BAU-Bausteine (die stehen nicht mehr im Plan) */}
                      {(schritt.mitgeltende_dokumente?.length || 0) > 0 && (
                        <span
                          className="inline-flex items-center gap-1 min-w-0 max-w-full text-[11px] text-indigo-700 px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200"
                          title={`Mitgeltende Dokumente (${schritt.mitgeltende_dokumente!.length}):\n${schritt.mitgeltende_dokumente!.join('\n')}`}
                        >
                          <FileText className="w-3 h-3 shrink-0" />
                          <span className="shrink-0">{schritt.mitgeltende_dokumente!.length}</span>
                          <span className="truncate">· {schritt.mitgeltende_dokumente!.map(n => kurzform(n)).join(', ')}</span>
                        </span>
                      )}
                      {(schritt.schutzmassnahmen?.length || 0) > 0 && (
                        <span className="text-[11px] text-green-700 px-1.5 py-0.5 rounded bg-green-50 border border-green-200">{schritt.schutzmassnahmen!.length} Maßn.</span>
                      )}
                    </div>
                  </div>
                  {zeilenKnoepfe && (
                    <>
                      <SchrittVerschiebenAuswahl
                        aktuell={gruppe.schluessel}
                        ziele={ziele}
                        gesperrt={gesperrt}
                        laeuft={vorgang?.schrittId === schritt.id}
                        onVerschieben={ziel => verschiebeSchritt(schritt.id, ziel)}
                        onNeuerAbschnitt={() => { setDeleteConfirmId(null); setNeuerAbschnittFuer(schritt.id); }}
                      />
                      <button
                        onClick={(e) => openEdit(schritt, e)}
                        disabled={gesperrt}
                        className="opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 p-1 text-slate-400 hover:text-blue-500 rounded"
                        title="Arbeitsschritt bearbeiten"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setFehler(null); setNeuerAbschnittFuer(null); setDeleteConfirmId(deleteConfirmId === schritt.id ? null : schritt.id); }}
                        disabled={gesperrt}
                        className="opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 p-1 text-slate-400 hover:text-red-500 rounded"
                        title="Arbeitsschritt löschen"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
                {zeilenKnoepfe && neuerAbschnittFuer === schritt.id && (
                  <SchrittNeuerAbschnittEingabe
                    gesperrt={gesperrt}
                    onAnlegen={name => verschiebeInNeuenAbschnitt(schritt.id, name)}
                    onAbbrechen={() => setNeuerAbschnittFuer(null)}
                  />
                )}
                {zeilenKnoepfe && deleteConfirmId === schritt.id && (
                  <div className="mt-1 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-900 space-y-2">
                    <div>
                      <strong>„{schritt.bezeichnung}" löschen?</strong>
                      <p className="mt-1 text-[11px] text-red-800">
                        Der Arbeitsschritt wird mitsamt seinen Gefährdungen und Schutzmaßnahmen dauerhaft entfernt.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(schritt.id)}
                        disabled={deletingId === schritt.id}
                        className="border-red-300 text-red-700 hover:bg-red-100"
                      >
                        {deletingId === schritt.id && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                        Endgültig löschen
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(null)} disabled={deletingId === schritt.id}>
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                )}
                </React.Fragment>
                );
              })}
            </div>
          </div>
        ))}
        {schritte.length === 0 && (
          <div className="text-sm text-slate-400 italic p-4 text-center">
            {isReadOnly ? 'Keine Arbeitsschritte.' : 'Keine Arbeitsschritte. Über „Muster" Standard-Zeilen übernehmen oder neu anlegen.'}
          </div>
        )}
      </div>

      {!isReadOnly && (
        <div className="p-3 border-t border-slate-200 bg-white">
          <Button data-tutorial="sigeplan.schritt-neu" variant="outline" className="w-full text-slate-600 border-dashed" onClick={openNew} disabled={gesperrt || auswahlModus}>
            <Plus className="w-4 h-4 mr-2" /> Neuer Schritt
          </Button>
        </div>
      )}

      {editorOpen && !isReadOnly && (
        <div className="fixed inset-0 z-[160] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl flex flex-col max-h-[92vh]">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
              <h2 className="text-lg font-bold text-slate-800">
                {zusammenfuehren ? 'Arbeitsschritte zusammenführen' : editingId ? 'Arbeitsschritt bearbeiten' : 'Neuer Arbeitsschritt'}
              </h2>
              <button onClick={schliesseEditor} disabled={saving} className="p-2 hover:bg-slate-200 rounded-lg text-slate-500 disabled:opacity-50">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-5">
              {zusammenfuehren && (
                <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {zusammenfuehrHinweis(zusammenfuehren.quellIds.map(id => ({ bezeichnung: schrittNachId.get(id)?.bezeichnung ?? 'unbekannt' })))}
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">Hauptabschnitt</label>
                  <div className="flex gap-2">
                    <select
                      className={inputCls + ' bg-white flex-1'}
                      value={form.hauptabschnitt || ''}
                      onChange={e => setForm({ ...form, hauptabschnitt: e.target.value })}
                    >
                      <option value="">-- Ohne Abschnitt --</option>
                      {/* Auch Abschnitte, die nur in Schritten vorkommen — sonst zeigte die Auswahl beim Bearbeiten nichts an */}
                      {ziele.filter(z => z.schluessel).map(z => <option key={z.schluessel} value={z.schluessel}>{z.name}</option>)}
                    </select>
                    <Button variant="outline" size="sm" onClick={() => setAbschnittDialog(!abschnittDialog)} title="Neuen Hauptabschnitt anlegen (global, für alle)">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {abschnittDialog && (
                    <div className="flex gap-2 mt-2">
                      <input
                        className={inputCls}
                        placeholder="Neuer Hauptabschnitt (gilt für alle Projekte)..."
                        value={neuerAbschnitt}
                        onChange={e => setNeuerAbschnitt(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleNeuerAbschnitt(); } }}
                      />
                      <Button variant="primary" size="sm" onClick={handleNeuerAbschnitt}>Anlegen</Button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Arbeiten / Leistungen (Bezeichnung) *</label>
                  <input data-tutorial="sigeplan.schritt-bezeichnung" className={inputCls} value={form.bezeichnung} onChange={e => handleBezeichnung(e.target.value)} placeholder="Aus Katalog wählen oder frei eingeben..." list="arbeiten-katalog" />
                  <datalist id="arbeiten-katalog">
                    {katalog.map((k, i) => <option key={k.id || `m-${i}`} value={k.bezeichnung} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Gewerk</label>
                  <input className={inputCls} value={form.gewerk} onChange={e => setForm(mitVorbelegung({ ...form, gewerk: e.target.value }))} placeholder="z.B. Gerüstbauer" list="gewerke-liste" />
                </div>
                {/* Ohne Zeitachse gesperrt, die Werte bleiben aber stehen (nicht löschen) */}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Zeitraum von</label>
                  <input type="date" className={inputCls + ' disabled:bg-slate-50 disabled:text-slate-400'} value={form.zeitraum_von} min={projektVon} max={projektBis}
                    disabled={ohneZeitachse}
                    onChange={e => setForm({ ...form, zeitraum_von: clampDatum(e.target.value, projektVon, projektBis) })} />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Zeitraum bis</label>
                  <input type="date" className={inputCls + ' disabled:bg-slate-50 disabled:text-slate-400'} value={form.zeitraum_bis} min={projektVon} max={projektBis}
                    disabled={ohneZeitachse}
                    onChange={e => setForm({ ...form, zeitraum_bis: clampDatum(e.target.value, projektVon, projektBis) })} />
                </div>
                {ohneZeitachse && (
                  <p className="md:col-span-2 -mt-2 text-[11px] text-slate-500 flex items-center gap-1">
                    <CalendarOff className="w-3 h-3 shrink-0" />
                    Bauzeitenplan liegt noch nicht vor – der Plan druckt keine Zeiträume (Haken im Stempelfeld)
                  </p>
                )}
              </div>

              <StringListEditor
                label="Schutzeinrichtungen / Schutzmaßnahmen"
                items={form.schutzmassnahmen || []}
                placeholder="z.B. 3-teiliger Seitenschutz..."
                onChange={items => setForm({ ...form, schutzmassnahmen: items })}
              />

              <StringListEditor
                label="Arbeitsvorschriften"
                items={form.vorschriften || []}
                placeholder="z.B. DGUV Vorschrift 38..."
                onChange={items => setForm({ ...form, vorschriften: items })}
              />

              <MitgeltendeDokumenteEditor
                titel="Mitgeltende Dokumente (im Plan unter den Vorschriften)"
                dokumente={form.mitgeltende_dokumente || []}
                onChange={handleDokumente}
                vorbelegt={editingId ? undefined : vorbelegung.automatisch}
                schritt={{
                  bezeichnung: form.bezeichnung,
                  gewerk: form.gewerk,
                  schutzmassnahmen: form.schutzmassnahmen,
                  vorschriften: form.vorschriften,
                }}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">Koordinierungsbedarf</label>
                  <input className={inputCls} value={form.koordinierungsbedarf} onChange={e => setForm({ ...form, koordinierungsbedarf: e.target.value })} />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={form.kritisch} onChange={e => setForm({ ...form, kritisch: e.target.checked })} />
                    Kritische Phase
                  </label>
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Zuständigkeit für Schutzeinrichtungen / Schutzmaßnahmen</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Erstellung</label>
                    <input className={inputCls} value={form.zustaendig_erstellung} onChange={e => setForm({ ...form, zustaendig_erstellung: e.target.value })} list="zustaendig-liste" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Kontrolle</label>
                    <input className={inputCls} value={form.zustaendig_kontrolle} onChange={e => setForm({ ...form, zustaendig_kontrolle: e.target.value })} list="zustaendig-liste" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Instandhaltung</label>
                    <input className={inputCls} value={form.zustaendig_instandhaltung} onChange={e => setForm({ ...form, zustaendig_instandhaltung: e.target.value })} list="zustaendig-liste" />
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Vorschläge stammen aus den Projektbeteiligten (Gewerk + Firma).</p>
              </div>

              {/* BG-BAU-Bausteine: stehen nicht mehr im Plan, nur noch im Handbuch
                  (PSA/Sicherheitszeichen) — deshalb zurückgenommen und ans Ende gestellt. */}
              <div className="pt-3 border-t border-dashed border-slate-200">
                <label className="block text-[11px] text-slate-400 mb-1">BG-BAU-Bausteine (nur für das Handbuch, nicht im Plan)</label>
                <div className="flex flex-wrap gap-1 mb-1">
                  {(form.hinweis_codes || []).map((c, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-slate-50 border border-slate-200 text-slate-500 rounded px-1.5 py-0.5">
                      {c}
                      <button onClick={() => setForm({ ...form, hinweis_codes: (form.hinweis_codes || []).filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="md:w-1/2">
                  <input
                    className="w-full text-xs p-1.5 border border-slate-200 rounded bg-slate-50 text-slate-600 outline-none focus:ring-2 focus:ring-blue-500/20"
                    value={neuerHinweis}
                    onChange={e => setNeuerHinweis(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        const c = neuerHinweis.trim().replace(/[;,]$/, '');
                        if (c) setForm({ ...form, hinweis_codes: [...(form.hinweis_codes || []), c] });
                        setNeuerHinweis('');
                      }
                    }}
                    onBlur={() => {
                      const c = neuerHinweis.trim();
                      if (c) {
                        setForm({ ...form, hinweis_codes: [...(form.hinweis_codes || []), c] });
                        setNeuerHinweis('');
                      }
                    }}
                    placeholder="z.B. B 113 — Enter fügt hinzu"
                    list="hinweis-codes-liste"
                  />
                  <datalist id="hinweis-codes-liste">
                    {hinweisOptionen.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>

              <datalist id="zustaendig-liste">
                {zustaendigOptionen.map(o => <option key={o} value={o} />)}
              </datalist>
              <datalist id="gewerke-liste">
                {Array.from(new Set(beteiligte.map(b => b.tradeType).filter(Boolean))).map(g => <option key={g} value={g!} />)}
              </datalist>
            </div>

            {/* Das Banner der Liste liegt hinter dem Dialog — Fehler hier noch einmal */}
            {fehler && (
              <div className="px-4 py-2 bg-red-50 border-t border-red-200 text-xs text-red-700">{fehler}</div>
            )}
            <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
              <Button variant="ghost" onClick={schliesseEditor} disabled={saving}>Abbrechen</Button>
              <Button
                data-tutorial="sigeplan.schritt-speichern"
                variant="primary"
                onClick={zusammenfuehren ? handleZusammenfuehren : handleSave}
                disabled={saving || !form.bezeichnung.trim()}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : zusammenfuehren ? <Merge className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {zusammenfuehren ? 'Zusammenführen' : 'Speichern'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {ergaenzung && !isReadOnly && (
        <div className="fixed inset-0 z-[160] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col max-h-[92vh]">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <FilePlus2 className="w-5 h-5 text-blue-600" />
                Unterlagen ergänzen
              </h2>
              <button onClick={() => setErgaenzung(null)} disabled={!!ergaenzungLaeuft} className="p-2 hover:bg-slate-200 rounded-lg text-slate-500 disabled:opacity-50">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 flex-1 overflow-y-auto space-y-3">
              <p className="text-sm text-slate-600">
                Zu allen Arbeitsschritten dieses Plans wurden passende mitgeltende Dokumente aus dem Katalog ermittelt.
                Es wird nur ergänzt – vorhandene Einträge bleiben unverändert, nichts wird entfernt. Abgewählte Dokumente werden nicht übernommen.
              </p>
              {ergaenzung.posten.some(p => p.neu.some(istBedingt)) && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  Vorschläge mit Bedingung („Nur …“) sind nicht vorausgewählt – bitte nur anhaken, wenn die Bedingung auf dieser Baustelle zutrifft.
                </p>
              )}

              {ergaenzung.posten.length === 0 ? (
                <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
                  Keine Ergänzungen: Zu den {anzahl(schritte.length, 'Arbeitsschritt', 'Arbeitsschritten')} liegen keine weiteren Vorschläge aus dem Katalog vor.
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    <span className="font-semibold text-slate-700">
                      {anzahl(ergaenzungAuswahl.dokumente, 'Dokument', 'Dokumente')} für {ergaenzungAuswahl.schritte} von {anzahl(schritte.length, 'Arbeitsschritt', 'Arbeitsschritten')} ausgewählt
                    </span>
                    <span className="ml-auto flex gap-1">
                      <button type="button" onClick={() => alleAuswaehlen(true)} disabled={!!ergaenzungLaeuft} className="text-blue-600 hover:bg-blue-50 rounded px-1.5 py-1">Alle</button>
                      <button type="button" onClick={() => alleAuswaehlen(false)} disabled={!!ergaenzungLaeuft} className="text-blue-600 hover:bg-blue-50 rounded px-1.5 py-1">Keine</button>
                    </span>
                  </div>
                  <div className="space-y-2">
                    {ergaenzung.posten.map(p => (
                      <div key={p.schrittId} className="border border-slate-200 rounded-lg p-3">
                        {p.hauptabschnitt && (
                          <div className="text-[11px] text-slate-400 uppercase tracking-wider truncate" title={p.hauptabschnitt}>{p.hauptabschnitt}</div>
                        )}
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="font-medium text-sm text-slate-800 break-words">{p.bezeichnung}</span>
                          <span className="text-[11px] text-slate-400">
                            {p.vorhanden.length ? `bisher ${anzahl(p.vorhanden.length, 'Dokument', 'Dokumente')}` : 'bisher keine Dokumente'}
                            {` · +${p.neu.length}`}
                          </span>
                        </div>
                        <div className="mt-1.5 space-y-0.5">
                          {p.neu.map(v => {
                            const k = auswahlSchluessel(p.schrittId, v.dokument.name);
                            const gewaehlt = !ergaenzung.abgewaehlt.has(k);
                            return (
                              <label key={k} className="flex items-start gap-2 text-sm cursor-pointer rounded px-1.5 py-1 hover:bg-slate-50" title={v.dokument.wannBenoetigt}>
                                <input
                                  type="checkbox"
                                  className="mt-1 shrink-0"
                                  checked={gewaehlt}
                                  disabled={!!ergaenzungLaeuft}
                                  onChange={() => umschaltenAuswahl(p.schrittId, v.dokument.name)}
                                />
                                <span className="flex-1 min-w-0">
                                  <span className={gewaehlt ? 'text-slate-800' : 'text-slate-400 line-through'}>{v.dokument.name}</span>
                                  {v.dokument.kurz && <span className="ml-1.5 text-[11px] text-slate-400">({v.dokument.kurz})</span>}
                                  {v.bedingungen.map(b => (
                                    <span key={b} className="block text-[11px] text-amber-700">{bedingungsHinweis(b)}</span>
                                  ))}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="p-4 border-t border-slate-200 bg-slate-50 flex flex-wrap items-center justify-end gap-3">
              {ergaenzungLaeuft && (
                <span className="mr-auto text-xs text-slate-500 flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Speichere {ergaenzungLaeuft.erledigt} / {ergaenzungLaeuft.gesamt} …
                </span>
              )}
              <Button variant="ghost" onClick={() => setErgaenzung(null)} disabled={!!ergaenzungLaeuft}>
                {ergaenzung.posten.length ? 'Abbrechen' : 'Schließen'}
              </Button>
              {ergaenzung.posten.length > 0 && (
                <Button variant="primary" onClick={ergaenzungAnwenden} disabled={!!ergaenzungLaeuft || ergaenzungAuswahl.dokumente === 0}>
                  {ergaenzungLaeuft ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FilePlus2 className="w-4 h-4 mr-2" />}
                  {anzahl(ergaenzungAuswahl.dokumente, 'Dokument', 'Dokumente')} ergänzen
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
